import {
    Shipment,
    ShipmentDetail,
    ShipmentListItem,
    ShipmentStatus,
    ShipmentFilter
} from '../types/shipment.types';
import { Organization } from '../types/organization.types';

/**
 * Maps API response to our frontend ShipmentDetail model
 * Handles field name inconsistencies and data type conversions
 */
export const mapShipmentFromAPI = (apiShipment: any): ShipmentDetail => {
    console.log('Mapping shipment:', apiShipment);

    // If we don't have a valid API response, return a default object
    if (!apiShipment || typeof apiShipment !== 'object') {
        console.error('Invalid shipment data received from API');
        return {
            id: 0,
            name: 'Error retrieving shipment',
            status: ShipmentStatus.CREATED,
            orderedBoxes: 0,
            donations: 0,
            amount: 0,
            donatedBars: 0,
            fulfilledBoxes: 0,
            campaignName: 'Unknown',
            campaignId: 0,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            organization: 'Unknown',
            logisticsDetails: '',
            notifyDonors: false,
            active: false,
            shipmentAlerts: 0,
            languageCode: 'en',
            address: 'Unknown',
            latitude: 0,
            longitude: 0,
            createdByEmail: '',
            createdByName: '',
            supplierId: '',
            supplierCode: '',
            label: ''
        };
    }

    // Check for campaign object in the response
    const campaign = apiShipment.campaign || {};

    // Calculate values based on campaign data if available
    const campaignAmountCollected = campaign.amountCollected || 0;
    const campaignTargetAmount = campaign.targetAmount || 0;
    const orderedBoxUnit = apiShipment.orderedBoxUnit || 50; // Default to 50 if not provided

    // Default to empty arrays if user or Donation values are not present
    const userDonations = apiShipment.user?.Donation || [];

    // Use campaign.donationAidUnits for donated bars according to API mapping
    const donatedBars = campaign.donationAidUnits !== undefined
        ? campaign.donationAidUnits
        : userDonations.reduce((sum: number, d: any) => sum + (d.numberOfUnits || 0), 0) || 0;

    // Calculate total donation amount using totalAmountUSD according to API mapping
    const amount = campaign.totalAmountUSD !== undefined
        ? campaign.totalAmountUSD
        : campaign.amountCollected !== undefined
            ? campaign.amountCollected
            : userDonations.reduce((sum: number, d: any) => sum + (d.amount || 0), 0) || 0;

    // Handle status value - map from API format or use default
    let status: ShipmentStatus;
    // Note the typo in API field name: "currentStutus" instead of "currentStatus"
    const statusValue = apiShipment.currentStutus;

    // Map API status (could be uppercase) to our enum values
    if (statusValue && typeof statusValue === 'string') {
        switch (statusValue.toUpperCase()) {
            case 'SHIPPED':
                status = ShipmentStatus.SHIPPED;
                break;
            case 'RECEIVED':
            case 'MISSING':
                status = ShipmentStatus.MISSING;
                break;
            case 'DELIVERED':
                status = ShipmentStatus.DELIVERED;
                break;
            case 'CREATED':
                status = ShipmentStatus.CREATED;
                break;
            default:
                // Try to convert to proper case as fallback
                const properCase = statusValue.charAt(0).toUpperCase() + statusValue.slice(1).toLowerCase();
                if (Object.values(ShipmentStatus).includes(properCase as ShipmentStatus)) {
                    status = properCase as ShipmentStatus;
                } else {
                    console.log(`Unknown status value: ${statusValue}, defaulting to CREATED`);
                    status = ShipmentStatus.CREATED;
                }
        }
    } else {
        status = ShipmentStatus.CREATED; // Default if missing
    }

    // Calculate ordered and fulfilled boxes based on campaign data if available
    const calculatedOrderedBoxes = campaign.targetAmount && orderedBoxUnit
        ? Math.floor(campaign.targetAmount / orderedBoxUnit)
        : apiShipment.orderedBoxUnit || 0;

    const calculatedFulfilledBoxes = campaign.amountCollected && orderedBoxUnit
        ? Math.floor(campaign.amountCollected / orderedBoxUnit)
        : (status === ShipmentStatus.SHIPPED || status === ShipmentStatus.MISSING || status === ShipmentStatus.DELIVERED)
            ? (apiShipment.orderedBoxUnit || 50)
            : 0;

    return {
        id: apiShipment.id || 0,
        supplierId: apiShipment.supplierId || '',
        supplierCode: apiShipment.supplierCode || '',
        name: apiShipment.name || 'Unnamed Shipment',
        label: apiShipment.label || '',
        status: status as ShipmentStatus,
        orderedBoxes: calculatedOrderedBoxes,
        notifyDonors: apiShipment.notifyDoner || false,
        active: apiShipment.active || false,
        shipmentAlerts: apiShipment.shipmentAlerts || 0,
        logisticsDetails: apiShipment.logisticsDetails || null,
        // Extract organization name from either string or object
        organization: (() => {
            // Try orgenization field first (API typo)
            const orgField = apiShipment.orgenization;
            if (typeof orgField === 'string') {
                return orgField;
            } else if (orgField && typeof orgField === 'object') {
                // Check if it matches our Organization type
                const orgObject = orgField as Organization;
                if (orgObject.name) {
                    return orgObject.name;
                }
            }

            // Try organization field (correct spelling)
            const orgCorrectField = apiShipment.organization;
            if (typeof orgCorrectField === 'string') {
                return orgCorrectField;
            } else if (orgCorrectField && typeof orgCorrectField === 'object') {
                // Check if it matches our Organization type
                const orgObject = orgCorrectField as Organization;
                if (orgObject.name) {
                    return orgObject.name;
                }
            }

            // Default if neither field is present
            return 'Unknown Organization';
        })(),
        languageCode: apiShipment.languageCode || 'EN',
        createdAt: apiShipment.createAt || new Date().toISOString(), // Note the typo in API field name: "createAt"
        updatedAt: apiShipment.updatedAt || new Date().toISOString(),
        address: apiShipment.address || '',
        // Handle coordinates, converting strings to numbers and handling the longitude typo in API
        latitude: parseFloat(apiShipment.latitude || '0'),
        longitude: parseFloat(apiShipment.longtitude || '0'),

        // Derived fields
        campaignName: campaign.name || apiShipment.user?.Campaigns?.[0]?.name || 'No Campaign',
        campaignId: campaign.id || apiShipment.user?.Campaigns?.[0]?.id || null,
        donatedBars,
        donations: campaign.numberOfDonations !== undefined ? campaign.numberOfDonations : userDonations.length || 0,
        amount,
        createdByEmail: apiShipment.user?.email || '',
        createdByName: apiShipment.user?.name || '',
        // Use calculated fulfilled boxes
        fulfilledBoxes: calculatedFulfilledBoxes,

        // Original fields that might be needed for detailed view
        user: apiShipment.user || null,
        products: apiShipment.products || [],
        createdBy: apiShipment.createdBy || null
    };
};

/**
 * Maps frontend filter object to the API expected filter parameters
 * Handles field name inconsistencies
 */
export const mapFiltersToAPI = (filters: ShipmentFilter): Record<string, any> => {
    const apiFilters: Record<string, any> = {};

    // Add filter parameters
    if (filters.campaign) apiFilters.campaign = filters.campaign; // Map campaign filter to API's campaign parameter
    if (filters.status?.length) apiFilters.status = filters.status.join(',');
    if (filters.organization) apiFilters.organization = filters.organization;
    if (filters.dateFrom) apiFilters.dateFrom = filters.dateFrom;
    if (filters.dateTo) apiFilters.dateTo = filters.dateTo;
    if (filters.showDeactivated !== undefined) apiFilters.showDeactivated = filters.showDeactivated;
    if (filters.page) apiFilters.page = filters.page;
    if (filters.limit) apiFilters.limit = filters.limit;
    if (filters.search) apiFilters.search = filters.search; // For name searching

    // Map field name changes for sorting
    if (filters.sortBy) {
        // Map frontend field names to API field names
        switch (filters.sortBy) {
            case 'createdAt':
                apiFilters.sortBy = 'createAt'; // API field has typo
                break;
            case 'status':
                apiFilters.sortBy = 'currentStutus'; // API field has typo
                break;
            case 'name':
                apiFilters.sortBy = 'name'; // Shipment name
                break;
            case 'campaignName':
                apiFilters.sortBy = 'campaign.name'; // Campaign name in the nested object
                break;
            default:
                apiFilters.sortBy = filters.sortBy; // Pass through other fields
        }
    }

    // Add sort order
    if (filters.sortOrder) apiFilters.sortOrder = filters.sortOrder;

    // Debug log to assist in troubleshooting
    console.log('Filtering shipments with:', apiFilters);

    return apiFilters;
};

/**
 * Maps ShipmentDetail to simplified ShipmentListItem for the table view
 */
export const toShipmentListItem = (detail: ShipmentDetail): ShipmentListItem => {
    return {
        id: detail.id,
        name: detail.name,
        campaignName: detail.campaignName,
        organization: detail.organization,
        donatedBars: detail.donatedBars,
        orderedBoxes: detail.orderedBoxes,
        fulfilledBoxes: detail.fulfilledBoxes,
        status: detail.status,
        createdAt: detail.createdAt
    };
};

/**
 * Maps ShipmentDetail to the legacy Shipment interface for backward compatibility
 */
export const toShipmentLegacy = (detail: ShipmentDetail): Shipment => {
    return {
        id: detail.id.toString(),
        location: detail.address,
        name: detail.name,
        campaign: detail.campaignName,
        item: '', // Not directly mapped, needs to be set by caller if needed
        quantity: detail.orderedBoxes.toString(),
        organization: detail.organization,
        logistics: detail.logisticsDetails,
        notifyDonors: detail.notifyDonors
    };
};

/**
 * Maps legacy Shipment to partial ShipmentDetail (for updates)
 */
export const fromShipmentLegacy = (legacy: Shipment): Partial<ShipmentDetail> => {
    return {
        id: parseInt(legacy.id, 10) || 0,
        name: legacy.name,
        address: legacy.location,
        orderedBoxes: parseInt(legacy.quantity, 10) || 0,
        organization: legacy.organization,
        logisticsDetails: legacy.logistics,
        notifyDonors: legacy.notifyDonors
    };
};

/**
 * Maps UI model to API model for create/update operations
 * Handles field name inconsistencies for sending to API
 */
export const mapShipmentToAPI = (shipment: Partial<ShipmentDetail>): Record<string, any> => {
    const apiPayload: Record<string, any> = {
        ...(shipment.id !== undefined && { id: shipment.id }),
        ...(shipment.name !== undefined && { name: shipment.name }),
        ...(shipment.label !== undefined && { label: shipment.label }),
        ...(shipment.status !== undefined && { currentStutus: shipment.status }), // API typo
        ...(shipment.orderedBoxes !== undefined && { orderedBoxUnit: shipment.orderedBoxes }), // API field name
        ...(shipment.notifyDonors !== undefined && { notifyDoner: shipment.notifyDonors }), // API typo
        ...(shipment.active !== undefined && { active: shipment.active }),
        ...(shipment.shipmentAlerts !== undefined && { shipmentAlerts: shipment.shipmentAlerts }),
        ...(shipment.logisticsDetails !== undefined && { logisticsDetails: shipment.logisticsDetails }),
        // Handle organization - keep as string (name only) since we've extracted just the name in mapShipmentFromAPI
        ...(shipment.organization !== undefined && { orgenization: shipment.organization }), // API typo
        ...(shipment.languageCode !== undefined && { languageCode: shipment.languageCode }),
        ...(shipment.address !== undefined && { address: shipment.address }),
        ...(shipment.campaignId !== undefined && { campaign: shipment.campaignId }), // Map campaignId to campaign for API
    };

    // Convert number fields to strings as needed by API
    if (shipment.latitude !== undefined) {
        apiPayload.latitude = String(shipment.latitude);
    }

    if (shipment.longitude !== undefined) {
        apiPayload.longtitude = String(shipment.longitude); // API typo
    }

    return apiPayload;
};